import { LitElement, TemplateResult, html, nothing } from 'lit';
import { property, customElement, query, state } from 'lit/decorators.js';
import { styles } from '../view/styles';
import {
  ButtonColor,
  ButtonColors,
  ButtonFontSize,
  ButtonFontSizes,
  ButtonTheme,
  ButtonThemes,
  ButtonType,
  ButtonTypes,
} from '../../gzButton/types';
import { translateFn } from 'apps/webcomponents/src/utils/translator.helper';
import { ListItem } from '../../gzSingleSelectList/types';
import { GzGenericOverlayWebComponent } from '../../gzGenericOverlay';
import { GzSingleSelectListWebComponent } from '../../gzSingleSelectList';
import { GzSelectMenuWithButtonEvents } from '../types';
import { LoadMoreOnScrollController } from '../../reactiveControllers/LoadMoreOnScrollController';

@customElement('gz-select-menu-with-button')
export class GzSelectMenuWithButtonWebComponent extends LitElement {
  /**
   * The default button theme is light
   */
  @property({ type: String, attribute: true, reflect: true })
  theme: ButtonTheme = ButtonThemes.LIGHT;

  @property({ type: String, attribute: true, reflect: true })
  buttonType: ButtonType = ButtonTypes.QUIET;

  /**
   * The default button color theme is blue
   */
  @property({ type: String, attribute: true, reflect: true })
  buttonColor: ButtonColor = ButtonColors.PRIMARY;

  /**
   * The default button font size is 14px.
   */
  @property({ type: String, attribute: true, reflect: true })
  buttonFontSize: ButtonFontSize = ButtonFontSizes.MEDIUM;

  @property({ type: String, attribute: true, reflect: true })
  btnText = '';

  @property({ type: Boolean, attribute: true, reflect: true })
  isLoading = false;

  @property({ type: Array, attribute: true, reflect: true })
  filterOptions: ListItem[] = [];

  @property({ type: Boolean, attribute: true, reflect: true })
  disabled = false;

  @property({ type: Boolean, attribute: true, reflect: true })
  hasDynamicSearch = false;

  @state() hideMenu = false;

  /**
   * Whether there are more items to load. This will be set to true/false based on the response from the backend call
   */
  @property({ type: Boolean })
  hasMoreItems = false;

  loadMoreItems?: () => Promise<ListItem[]>;

  /**
   * The sentinel element will trigger the load more callback when it's scrolled into view.
   * It should be placed at the end of the list.
   * @private
   */
  @query('#sentinel')
  private readonly sentinel!: HTMLDivElement;

  @state() private scrolledToBottom = false;

  @state() searchString = '';

  @state() private scrolledLocation = 0;

  @query('gz-generic-overlay') overlay!: GzGenericOverlayWebComponent;

  @query('gz-single-select-list') selectList!: GzSingleSelectListWebComponent;

  static get styles() {
    return styles;
  }

  private readonly loadMoreController = new LoadMoreOnScrollController(this, {
    onLoadMore: () => this.onLoadMore(),
    // IntersectionObserver options
    observerOptions: {
      root: null, // or this if the component is scrollable
      threshold: 0.1,
    },
    getSentinelElement: () => this.sentinel,
  });

  private async onLoadMore(): Promise<void> {
    // If we're already loading, skip
    if (this.isLoading) {
      return undefined;
    }

    if (this.loadMoreItems) {
      this.isLoading = true;

      const newItems = await this.loadMoreItems();

      if (newItems.length === 0) {
        // No more items => remove sentinel, which will stop the controller from firing
        this.hasMoreItems = false;
      } else {
        this.filterOptions = [...this.filterOptions, ...newItems];
      }

      // Done fetching
      this.isLoading = false;
      this.scrolledToBottom = false;
      this.scrollToLastSaved();
    }
  }

  private scrollToLastSaved() {
    this.updateComplete.then(() => {
      setTimeout(() => {
        this.selectList.setListScrollPosition(this.scrolledLocation);
      }, 50);
    });
  }

  render(): TemplateResult {
    return html`<div>
      <gz-generic-overlay
        .attachToBody="${false}"
        .overlayID="default-overlay"
        @generic-overlay-open="${() => {
          this.hideMenu = false;
        }}"
        @generic-overlay-close="${this.onOverlayClose}"
      >
        ${this.renderButton()}
        <div id="list-wrapper">
          ${this.renderSelectList()}
          ${this.hasMoreItems
            ? html` <div id="sentinel" style="display: ${this.getSentinelDisplay()}"></div>`
            : nothing}
        </div>
      </gz-generic-overlay>
    </div> `;
  }

  disconnectedCallback(): void {
    super.disconnectedCallback();
    this.onOverlayClose(new Event(''));
  }

  private onOverlayClose(e: Event): void {
    this.selectList.clearSearchInput(e);
    this.selectList.filterOptions = [...this.filterOptions];
  }

  private getSentinelDisplay(): string {
    return this.scrolledToBottom && !this.searchString ? 'block' : 'none';
  }

  protected renderButton(): TemplateResult {
    return html`<gz-button
      @click="${this.onTriggerButtonClick}"
      slot="target"
      class="menu-button"
      type="${this.buttonType}"
      color="${this.buttonColor}"
      buttonFontSize="${this.buttonFontSize}"
      >${this.btnText}<span class="menu-action-icon ds-icon-dropdown_arrow"></span
    ></gz-button>`;
  }

  protected renderSelectList(): TemplateResult {
    if (this.hideMenu) {
      return html``;
    }

    return html` <gz-single-select-list
      id="single-selection-list"
      .isLoading="${this.isLoading}"
      .filterOptions="${this.filterOptions}"
      class="single-select-list"
      searchPlaceholderString="${translateFn('Search')}"
      noSearchResultsString="${translateFn('No options found')}"
      @selectionChange="${this.handleListSelectionChange}"
      shouldHighlightSearchStrings
      ?hasDynamicSearch="${this.hasDynamicSearch}"
      @listScroll="${this.checkIfSentinelIsInView}"
      @optionsFiltered="${this.onSearchInputChange}"
      @searchInputChange="${this.onSearchInputChange}"
    ></<gz-single-select-list>`;
  }

  private onSearchInputChange(e: CustomEvent): void {
    this.searchString = e.detail.value;
  }

  private onTriggerButtonClick(e: Event): void {
    this.overlay.openOverlay(e.target as HTMLElement);

    this.dispatchMenuEvent(GzSelectMenuWithButtonEvents.CLICK, {});
  }

  protected handleListSelectionChange(e: CustomEvent): void {
    const selectedValue = e.detail.selectedValue;
    this.overlay.closeOverlay();

    this.dispatchMenuEvent(GzSelectMenuWithButtonEvents.CHANGE, {
      optionLabel: this.filterOptions.find((option: ListItem): boolean => option.value === selectedValue)?.label,
      id: selectedValue,
    });

    this.hideMenu = true;
    this.clearSelection();
  }

  private dispatchMenuEvent(event: GzSelectMenuWithButtonEvents, detail: any): void {
    this.dispatchEvent(
      new CustomEvent(event, {
        detail: detail,
        bubbles: true,
        composed: true,
      })
    );
  }

  private checkIfSentinelIsInView(e) {
    this.scrolledToBottom = e.detail.scrollTop + e.detail.offsetHeight >= e.detail.scrollHeight;
    this.scrolledLocation = e.detail.scrollTop;
  }

  private clearSelection(): void {
    if (this.selectList) {
      this.selectList.selectedOption = null;
    }
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'gz-select-menu-with-button': GzSelectMenuWithButtonWebComponent;
  }
}
